import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import authBg from "@/assets/opt/authbg.jpg";
import { toast } from "sonner";
import { SSO_STATE_KEY } from "@/lib/sso-shared";

/**
 * Tela de login única, usada tanto em `/auth` quanto na raiz `/`.
 * Renderizar na raiz evita um redirecionamento extra na entrada do site.
 */
export function AuthScreen() {
  const navigate = useNavigate();
  const [indisponivel, setIndisponivel] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const erro = params.get("erro") ?? params.get("error_description") ?? params.get("error");
    if (erro) toast.error(erro);
    if (params.get("indisponivel") === "1") {
      setIndisponivel(true);
    }
    if (erro || params.get("indisponivel")) {
      window.history.replaceState({}, "", "/auth");
    }
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: "/inicio", replace: true });
    });
  }, [navigate]);

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
      <div className="absolute inset-0 -z-10">
        <img
          src={authBg}
          alt="Vista do Kotel (Muro Ocidental) e da Cidade Antiga de Jerusalém ao entardecer"
          className="h-full w-full object-cover"
          width={1200}
          height={800}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/60 to-primary/85 dark:from-background/85 dark:via-background/75 dark:to-background/95" />
      </div>

      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <div className="rounded-2xl bg-white/95 px-8 py-6 shadow-xl backdrop-blur-sm dark:bg-card/90">
            <Logo size={110} />
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card/95 p-8 shadow-2xl backdrop-blur-sm">
          <h1 className="mb-6 text-center font-serif text-2xl font-semibold text-primary">
            Área de Membros
          </h1>
          {indisponivel && (
            <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm leading-relaxed text-destructive">
              O login único com a conta Beit está indisponível neste momento. Tente novamente em
              alguns minutos ou fale com a secretaria.
            </div>
          )}
          <GestaoSsoButton />
          <p className="mt-3 text-center text-xs leading-relaxed text-muted-foreground">
            Use a mesma conta e senha do sistema de cadastros da congregação.
          </p>
        </div>
        <p className="mt-6 rounded-lg bg-black/25 px-4 py-3 text-center text-xs leading-relaxed text-white/90 backdrop-blur-sm dark:bg-card/60 dark:text-muted-foreground">
          O acesso é exclusivo para membros no sistema de cadastros da Beit Tefilah Rechovot. Novas
          contas não são criadas aqui.{" "}
          <a
            href="https://cadastro.rechovot.org.br/auth?modo=cadastro"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block py-2 font-medium text-white underline underline-offset-4 hover:text-primary dark:text-foreground dark:hover:text-primary"
          >
            Cadastre-se para solicitar acesso
          </a>
          . Acesso mediante análise e aprovação da secretaria da Casa de Oração Rechovot.
        </p>
        <p className="mt-4 flex items-center justify-center gap-3 text-center text-sm text-white/90 drop-shadow dark:text-foreground/80">
          <Link
            to="/privacidade"
            className="inline-flex min-h-11 items-center px-2 hover:underline"
          >
            Política de privacidade
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/termos" className="inline-flex min-h-11 items-center px-2 hover:underline">
            Termos de uso
          </Link>
        </p>
      </div>
    </div>
  );
}

function GestaoSsoButton() {
  const [loading, setLoading] = useState(false);

  const handleSso = () => {
    setLoading(true);
    try {
      const state = crypto.randomUUID().replace(/-/g, "");
      sessionStorage.setItem(SSO_STATE_KEY, state);
      window.location.assign(`/sso/iniciar?state=${encodeURIComponent(state)}`);
    } catch {
      setLoading(false);
      toast.error("Não foi possível iniciar o login único. Tente novamente mais tarde.");
    }
  };

  return (
    <Button type="button" className="w-full" onClick={handleSso} disabled={loading}>
      {loading ? "Redirecionando..." : "Entrar com a conta Beit"}
    </Button>
  );
}
